import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IFunction, IUserPermissionsModel, IUserRentalTypes, IUserRights, IUserSalesTypes } from '../Shared/model/AdminUserModel';
import { ASLicenseSettings } from '../Shared/model/GeneralModel';
@Injectable({
    providedIn: 'root',
})

export class GeneralService {

    userGroupPermissions: EventEmitter<any> = new EventEmitter();
    private baseURL: string;
    public UserPermissions: IUserPermissionsModel;
    public ViewRightFalseMessage = $localize`:@@NotRightsToView:You do not have rights to View`;
    public CreateRightFalseMessage = $localize`:@@NotRightsToCreate:You do not have rights to Create New`;
    public EditRightFalseMessage = $localize`:@@NotRightsToEdit:You do not have rights to Edit`;
    public DeleteRightFalseMessage = $localize`:@@NotRightsToDelete:You do not have rights to Delete`;

    // AS Cloud License Settings
    public ASLicenseSettings: ASLicenseSettings | null = null;

    constructor(private http: HttpClient) {
        this.baseURL = environment.apiBaseUrl;
    }

    private userTerminal = new BehaviorSubject<number>(0); // default: Retail
    private dayEndTransData = new BehaviorSubject<any>({
        UserId: null,
        Terminal: null,
        OpeningCashDate: null,
        OcaNumSlNo: null
    });

    setTerminal(terminalId: number) {
        this.userTerminal.next(terminalId);
    }

    getTerminal() {
        return this.userTerminal;
    }

    getDayendRowDataForTrans() {
        return this.dayEndTransData.asObservable();
    }

    setDayendRowDataForTrans(data: any) {
        this.dayEndTransData.next(data);
    }

    getUserPermissionsForModule(functionId): IUserRights {
        let selectedFnUserRights: IUserRights = {
            CreateRight: false,
            ViewRight: false,
            EditRight: false,
            DeleteRight: false
        };
        if (this.UserPermissions) {
            const selectedFnRights: IFunction = this.UserPermissions.Functions.find(fun => fun.FunctionId == functionId);
            if (selectedFnRights) {
                selectedFnUserRights = selectedFnRights.UserRights;
            }
        }
        return selectedFnUserRights
    }

    getUserRentalTypes(): IUserRentalTypes[] {
        let userRentalTypes: IUserRentalTypes[] = [{
            RentalTypeAllowed: false,
            RentalTypeID: 0,
        }];

        if (this.UserPermissions && this.UserPermissions.UserRentalTypes) {
            userRentalTypes = this.UserPermissions.UserRentalTypes;
        }

        return userRentalTypes;
    }

    getUserSaleTypes(): IUserSalesTypes[] {
        let userSaleTypes: IUserSalesTypes[] = [{
            SalesTypeAllowed: false,
            SalesTypeID: 0,
        }];

        if (this.UserPermissions && this.UserPermissions.UserSalesTypes) {
            userSaleTypes = this.UserPermissions.UserSalesTypes;
        }
        return userSaleTypes;
    }

    getListOfValuesByType(typeId): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetListOfValuesByType?typeId=${typeId}`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as any;
                }),
                catchError(this.handleError)
            );
    }

    getLoggedInUserAndTerminal(): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetLoggedInUserAndTerminal`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as any;
                }),
                catchError(this.handleError)
            );
    }

    getCountries(): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetCountries`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    getCompanyId(): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetCompanyId`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    getStates(countryId): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetStates?countryId=${countryId}`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    getCompanyName(): Observable<string> {
        return this.http.get(
            `${this.baseURL}/General/GetCompanyName`,
            { responseType: 'text' }
        ).pipe(
            map(res => res as string),
            catchError(this.handleError)
        );
    }

    getTermsUsedInMarket(): Observable<any> {
        return this.http.get(
            `${this.baseURL}/General/GetTermsUsedInMarket`,
            { observe: 'response' }
        ).pipe(
            map((res: HttpResponse<any>) => res.body),
            catchError(this.handleError)
        );
    }

    getAllMarketCustomAndLicensingSettings(): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetAllMarketCustomAndLicensingSettings`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as any;
                }),
                catchError(this.handleError)
            );
    }

    getReportUrl(): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetReportUrl`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as any;
                }),
                catchError(this.handleError)
            );
    }

    GetAllAsmodules(): Observable<boolean> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetAllASmodules`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    const marketInfo = res.body as any;
                    return marketInfo;
                }),
                catchError(this.handleError)
            );
    }

    GetMarketCurrentTime(): Observable<string> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetMarketCurrentTime`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as string;
                }),
                catchError(this.handleError)
            );
    }

    private handleError(error: HttpErrorResponse) {
        console.error('server error:', error);
        if (error.error instanceof Error) {
            const errMessage = error.error.message;
            return throwError(errMessage);
        }
        return throwError(error || 'Node.js server error');
    }

    GetUserPermission(): Observable<IUserPermissionsModel> {
        return this.http.get<any>(
            `${this.baseURL}/User/GetUserPermission`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as any;
                }),
                catchError(this.handleError)
            );
    }

    getPOSSalesType(): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetPOSSalesType`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    getBarcodeTemplates(): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetBarcodeTemplates`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    authenticatePasscodeForUsers(userId: number, Passcode: string): Observable<any> {
        const url = `${this.baseURL}/General/AuthenticatePasscodeForUsers`;
        const body = { userId, Passcode };

        return this.http.post<any>(url, body, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        }).pipe(
            catchError(this.handleError)
        );
    }

    composeEmails(emailData): Observable<any> {
        const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
        const url = `${this.baseURL}/Email/ComposeEmails`;
        return this.http.post<any>(url, emailData, options)
            .pipe(
                map(res => {
                    const response = res as any;
                    return response;
                }),
                catchError(this.handleError)
            );
    }

    getCustomMarketSettings(): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetCustomMarketSettings`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    getASCloudLicenseSettings(): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/ASCloudLicenseSettings`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }
}
